import { api } from 'services/api';

import { Budget } from 'types/budget';
import { Composition } from 'types/composition';

export type BudgetBase = Budget['bases'][number];

export type BaseCompositionSearchResult = {
  base: BudgetBase;
  compositions: Composition[];
  error: boolean;
  page: number;
  lastPage: number;
  total: number;
};

type CompositionSearchParams = {
  'filter[resource]': 'composition';
  'filter[keyword]': string;
  'filter[base_id]': number;
  'filter[version_id]': number;
  'filter[locale_id]': number;
  'filter[price_type_id]': number;
  page: number;
  per_page: number;
};

export const DEFAULT_PER_PAGE = 10;

export const buildCompositionSearchParams = (
  base: BudgetBase,
  keyword: string,
  page = 1,
): CompositionSearchParams => {
  return {
    'filter[resource]': 'composition',
    'filter[keyword]': keyword,
    'filter[base_id]': base.version.base.id,
    'filter[version_id]': base.version.id,
    'filter[locale_id]': base.locale.id,
    'filter[price_type_id]': base.price_type.id,
    page,
    per_page: DEFAULT_PER_PAGE,
  };
};

type CompositionResponseData = {
  data?: Composition[];
  meta?: {
    last_page?: number;
    total?: number;
  };
  last_page?: number;
  total?: number;
};

const parseLastPage = (responseData: CompositionResponseData): number => {
  return responseData?.meta?.last_page || responseData?.last_page || 1;
};

const parseTotal = (responseData: CompositionResponseData): number => {
  if (typeof responseData?.meta?.total === 'number') {
    return responseData.meta.total;
  }

  if (typeof responseData?.total === 'number') {
    return responseData.total;
  }

  return responseData?.data?.length || 0;
};

export const searchCompositionsByBase = async (
  base: BudgetBase,
  keyword: string,
  page = 1,
): Promise<BaseCompositionSearchResult> => {
  const normalizedKeyword = keyword.trim();

  if (!normalizedKeyword) {
    return {
      base,
      compositions: [],
      error: false,
      page,
      lastPage: 1,
      total: 0,
    };
  }

  try {
    const response = await api.get('/composition', {
      params: buildCompositionSearchParams(base, normalizedKeyword, page),
    });
    const responseData = response.data;

    return {
      base,
      compositions: responseData?.data || [],
      error: false,
      page,
      lastPage: parseLastPage(responseData),
      total: parseTotal(responseData),
    };
  } catch (error) {
    return {
      base,
      compositions: [],
      error: true,
      page,
      lastPage: 1,
      total: 0,
    };
  }
};

export const searchCompositionsAcrossBases = async (
  bases: Budget['bases'],
  keyword: string,
  page = 1,
): Promise<BaseCompositionSearchResult[]> => {
  const normalizedKeyword = keyword.trim();

  if (!normalizedKeyword) {
    return [];
  }

  const requests = bases.map((base) => {
    return searchCompositionsByBase(base, normalizedKeyword, page);
  });

  return Promise.all(requests);
};
